<template>
  <div class="app-container">
    <!-- 考核期管理 -->
    <el-card shadow="never" style="margin-bottom: 15px;">
      <div slot="header" style="font-weight: bold; line-height: 1;">考核期管理</div>
      <div class="filter-container" style="margin-bottom: 10px;">
        <el-date-picker v-model="periodForm.year" type="year" placeholder="年份" value-format="yyyy" class="filter-item" style="width: 110px;" />
        <el-select v-model="periodForm.season" placeholder="季度" class="filter-item" style="width: 100px;">
          <el-option v-for="s in 4" :key="s" :label="'第' + s + '季度'" :value="s" />
        </el-select>
        <el-button v-if="canStart" v-waves class="filter-item" type="primary" icon="el-icon-video-play" :loading="savingPeriod" @click="openPeriod">
          开启考核
        </el-button>
      </div>
      <el-alert type="info" :closable="false" style="margin-bottom: 10px;"
        title="同一时间只允许一个考核期开启：开启新期次会自动关闭当前开启中的期次。重新开启已考核过的期次即为重新考核，打分人重新提交的评分会覆盖原记录（不会清空旧记录）。" />
      <el-table v-loading="periodLoading" :data="periods" border fit style="width: 100%;">
        <el-table-column label="考核期" min-width="140" align="center">
          <template slot-scope="{row}">{{ row.year }}年 第{{ row.season }}季度</template>
        </el-table-column>
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="{row}">
            <el-tag :type="row.enabled === 1 ? 'success' : 'info'">{{ row.enabled === 1 ? '开启中' : '已关闭' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="{row}">
            <template v-if="canStart">
              <el-button v-if="row.enabled === 1" type="warning" size="mini" @click="closePeriod(row)">关闭</el-button>
              <el-button v-else type="primary" size="mini" @click="openPeriodRow(row)">开启</el-button>
            </template>
            <span v-else style="color: #909399;">无权限</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 定量数据：动态行表格（新增一行→选部门→填目标值/实际值与占比） -->
    <el-card shadow="never">
      <div slot="header" style="font-weight: bold; line-height: 1;">定量数据</div>
      <div class="filter-container">
        <el-date-picker v-model="query.year" type="year" placeholder="年份" value-format="yyyy" class="filter-item" style="width: 110px;" />
        <el-select v-model="query.season" placeholder="季度" class="filter-item" style="width: 100px;">
          <el-option v-for="s in 4" :key="s" :label="'第' + s + '季度'" :value="s" />
        </el-select>
        <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="load">
          查询
        </el-button>
        <el-button v-if="canQuant" v-waves class="filter-item" type="success" icon="el-icon-plus" @click="addRow">
          新增一行
        </el-button>
      </div>

      <el-alert type="info" :closable="false" style="margin-bottom: 15px;"
        title="定量指标数据由计划财务部提供：点「新增一行」选择部门后，录入该部门的目标值、实际值与两项占比。两项占比必填且之和须为 100，随本期数据快照保存，其他期次改占比不影响本期。删除整行会同时清除该部门全部期次的数据，请谨慎操作。业务一部/业务二部填写本部门不良贷款数据（部门口径），其他被考核部门填写公司整体不良贷款数据（公司整体口径），部门名下方小标签标识口径。" />

      <el-table v-loading="loading" :data="rows" border fit style="width: 100%;">
        <el-table-column label="部门" width="170" align="center">
          <template slot-scope="{row}">
            <el-select v-if="row.isNew" v-model="row.deptId" placeholder="选择部门" size="mini" style="width: 140px;" :disabled="!canQuant">
              <el-option v-for="o in deptOptionsFor(row)" :key="o.deptId" :label="o.deptName" :value="o.deptId" />
            </el-select>
            <el-tag v-else type="success">{{ row.deptName }}</el-tag>
            <div v-if="scopeOf(row)" style="margin-top: 4px;">
              <el-tag size="mini" :type="scopeOf(row) === 'DEPT' ? '' : 'warning'">{{ scopeText(scopeOf(row)) }}</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="新增不良贷款目标值" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.aTarget" size="mini" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="新增不良贷款实际值" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.aActual" size="mini" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="新增不良贷款占比(%)" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.aWeight" size="mini" placeholder="必填" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="不良贷款余额目标值" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.bTarget" size="mini" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="不良贷款余额实际值" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.bActual" size="mini" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="不良贷款余额占比(%)" width="150" align="center">
          <template slot-scope="{row}">
            <el-input v-model="row.bWeight" size="mini" placeholder="必填" style="width: 120px;" :disabled="!canQuant" />
          </template>
        </el-table-column>
        <el-table-column label="A项得分" width="90" align="center">
          <template slot-scope="{row}">{{ fmtS(itemScore(row.aTarget, row.aActual)) }}</template>
        </el-table-column>
        <el-table-column label="B项得分" width="90" align="center">
          <template slot-scope="{row}">{{ fmtS(itemScore(row.bTarget, row.bActual)) }}</template>
        </el-table-column>
        <el-table-column label="定量得分" width="100" align="center">
          <template slot-scope="{row}">
            <span style="font-weight: bold; color: #409EFF;">{{ fmtS(rowScore(row)) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="{row}">
            <template v-if="canQuant">
              <el-button type="primary" size="mini" :loading="saving === row" @click="save(row)">保存</el-button>
              <el-button type="danger" size="mini" :loading="deleting === row" @click="removeRow(row)">删除</el-button>
            </template>
            <span v-else style="color: #909399;">无权限</span>
          </template>
        </el-table-column>
      </el-table>

      <div style="margin-top: 10px; color: #909399; font-size: 12px;">
        单项得分 = (1 - (实际数 - 目标数) / 目标数) × 100，结果限制在 0~100 之间；两项指标得分按占比合成定量指标得分参与部门得分计算；两项占比必填且之和须为 100，随各期记录快照保存。保存后需在考核结果页重新计算才生效。得分三列为页面按上述公式实时算出，保存时定量得分随数据原样落库，结果计算与高管打分参考直接取落库值。
      </div>
    </el-card>
  </div>
</template>

<script>
import { getQuantList, saveQuant, getPeriods, savePeriod, getQuantDepts, deleteQuantRow } from '../../api/performance'
import waves from '../../directive/waves'

export default {
  name: 'PerfConfig',
  directives: { waves },
  data() {
    const now = new Date()
    return {
      query: {
        year: String(now.getFullYear()),
        season: Math.floor(now.getMonth() / 3) + 1
      },
      periods: [],
      periodLoading: false,
      savingPeriod: false,
      periodForm: {
        year: String(now.getFullYear()),
        season: Math.floor(now.getMonth() / 3) + 1
      },
      rows: [],
      loading: false,
      saving: null,
      deleting: null,
      deptOptions: []
    }
  },
  created() {
    // 载入考核期列表，定量数据默认跟随开启中的期次
    this.loadPeriods(true)
    // 载入被考核部门下拉选项（定量数据行“选择部门”）
    this.loadDeptOptions()
  },
  computed: {
    /** 是否可开启/关闭考核（需 KAOHE_START 角色） */
    canStart() {
      const roles = this.$store.state.user.roles || []
      return roles.some(r => r.roleCode === 'KAOHE_START')
    },
    /** 是否可编辑定量数据（需 KAOHE_DINGLIANG_SETTING 角色） */
    canQuant() {
      const roles = this.$store.state.user.roles || []
      return roles.some(r => r.roleCode === 'KAOHE_DINGLIANG_SETTING')
    }
  },
  methods: {
    /** 载入考核期列表；setQuantDefault=true 时把定量区的默认期次设为开启中的期次 */
    loadPeriods(setQuantDefault) {
      this.periodLoading = true
      getPeriods().then(response => {
        this.periods = response.data || []
        this.periodLoading = false
        if (setQuantDefault) {
          const open = this.periods.find(p => p.enabled === 1)
          if (open) {
            this.query.year = String(open.year)
            this.query.season = open.season
          }
          this.load()
        }
      }).catch(() => {
        this.periodLoading = false
        if (setQuantDefault) this.load()
      })
    },
    /** 顶部“开启考核”：用所选年季开启 */
    openPeriod() {
      if (!this.periodForm.year || !this.periodForm.season) {
        this.$message.warning('请选择年份和季度')
        return
      }
      this.confirmOpen(Number(this.periodForm.year), this.periodForm.season)
    },
    /** 列表行“开启” */
    openPeriodRow(row) {
      this.confirmOpen(row.year, row.season)
    },
    confirmOpen(year, season) {
      const current = this.periods.find(p => p.enabled === 1)
      const name = year + '年 第' + season + '季度'
      let msg = '确认开启【' + name + '】考核？'
      if (current && (Number(current.year) !== Number(year) || Number(current.season) !== Number(season))) {
        msg += '当前开启中的【' + current.year + '年 第' + current.season + '季度】将自动关闭。'
      }
      msg += '开启后打分人可提交该期次的评分；重新开启已考核过的期次，打分人重新提交的评分将覆盖原记录。'
      this.$confirm(msg, '开启考核', {
        confirmButtonText: '确定开启',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => this.doSave(year, season, true)).catch(() => {})
    },
    closePeriod(row) {
      const name = row.year + '年 第' + row.season + '季度'
      this.$confirm('确认关闭【' + name + '】考核？关闭后打分人将无法提交评分（已评记录保留，仍可查看）。', '关闭考核', {
        confirmButtonText: '确定关闭',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => this.doSave(row.year, row.season, false)).catch(() => {})
    },
    doSave(year, season, enabled) {
      this.savingPeriod = true
      savePeriod({ year: Number(year), season: Number(season), enabled }).then(response => {
        this.savingPeriod = false
        this.periods = response.data || this.periods
        this.$notify({ title: '成功', message: enabled ? '考核已开启' : '考核已关闭', type: 'success', duration: 2000 })
      }).catch(() => {
        this.savingPeriod = false
      })
    },
    /** 载入被考核部门下拉选项（定量数据行“选择部门”） */
    loadDeptOptions() {
      getQuantDepts().then(response => {
        this.deptOptions = response.data || []
      }).catch(() => {})
    },
    /** 新增一行定量数据（部门待选，数值与占比留空） */
    addRow() {
      this.rows.push({
        deptId: null,
        deptName: '',
        isNew: true,
        aTarget: undefined,
        aActual: undefined,
        bTarget: undefined,
        bActual: undefined,
        aWeight: undefined,
        bWeight: undefined
      })
    },
    /** 当前行可选的部门（过滤掉其他行已占用的部门） */
    deptOptionsFor(row) {
      const used = this.rows.filter(r => r !== row && r.deptId).map(r => r.deptId)
      return this.deptOptions.filter(o => used.indexOf(o.deptId) === -1)
    },
    /** 定量数据口径：DEPT=部门口径（业务一部/二部），COMPANY=公司整体口径（其他被考核部门）；已保存行取后端 scope，新行按所选部门查下拉选项 */
    scopeOf(row) {
      if (row.scope) return row.scope
      if (!row.deptId) return null
      const o = this.deptOptions.find(x => x.deptId === row.deptId)
      return o ? o.scope : null
    },
    scopeText(scope) {
      return scope === 'DEPT' ? '部门口径' : '公司整体口径'
    },
    load() {
      if (!this.query.year || !this.query.season) {
        this.$message.warning('请选择年份和季度')
        return
      }
      this.loading = true
      getQuantList(this.query.year, this.query.season).then(response => {
        this.rows = (response.data || []).map(r => Object.assign({}, r, {
          isNew: false,
          aTarget: this.toNum(r.aTarget),
          aActual: this.toNum(r.aActual),
          bTarget: this.toNum(r.bTarget),
          bActual: this.toNum(r.bActual),
          aWeight: this.toNum(r.aWeight),
          bWeight: this.toNum(r.bWeight)
        }))
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    toNum(v) {
      return v === null || v === undefined ? undefined : Number(v)
    },
    /**
     * 解析输入框的值：去空格并把全角字符转半角；空→undefined；非数字→NaN。
     * el-input 绑定的是字符串，保存前统一转数字；非法内容明确报错，不再静默存成空值。
     */
    parseNum(v) {
      if (v === undefined || v === null) return undefined
      const s = String(v).trim()
        .replace(/[\uFF01-\uFF5E]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
        .replace(/。/g, '.')
      if (s === '') return undefined
      const n = Number(s)
      return isNaN(n) ? NaN : n
    },
    /** 定量单项分 = clamp((1-(实际-目标)/目标)×100, 0, 100)；目标为0时实际≤0记100、实际>0记0（与后端 PerfRule.quantItemScore 同口径） */
    itemScore(target, actual) {
      const t = this.parseNum(target)
      const a = this.parseNum(actual)
      if (t === undefined || a === undefined || Number.isNaN(t) || Number.isNaN(a)) return null
      if (t === 0) return a <= 0 ? 100 : 0
      let v = (1 - (a - t) / t) * 100
      if (v < 0) v = 0
      if (v > 100) v = 100
      return Math.round(v * 100) / 100
    },
    /** 定量合成分 = A单项分×A占比 + B单项分×B占比（2位小数）；单项分或占比缺失返回 null */
    rowScore(row) {
      const a = this.itemScore(row.aTarget, row.aActual)
      const b = this.itemScore(row.bTarget, row.bActual)
      const wa = this.parseNum(row.aWeight)
      const wb = this.parseNum(row.bWeight)
      if (a === null || b === null || wa === undefined || wb === undefined || Number.isNaN(wa) || Number.isNaN(wb)) return null
      return Math.round((a * wa / 100 + b * wb / 100) * 100) / 100
    },
    fmtS(v) {
      return v === null || v === undefined ? '-' : v
    },
    save(row) {
      if (!row.deptId) {
        this.$message.warning('请先选择部门')
        return
      }
      // 六项逐一解析：目标/实际值可为小数、可留空；占比须为 0~100 整数且必填
      const fields = [
        ['aTarget', '新增不良贷款目标值', false],
        ['aActual', '新增不良贷款实际值', false],
        ['bTarget', '不良贷款余额目标值', false],
        ['bActual', '不良贷款余额实际值', false],
        ['aWeight', '新增不良贷款占比', true],
        ['bWeight', '不良贷款余额占比', true]
      ]
      const vals = {}
      for (let i = 0; i < fields.length; i++) {
        const key = fields[i][0]
        const label = fields[i][1]
        const isWeight = fields[i][2]
        const n = this.parseNum(row[key])
        if (Number.isNaN(n)) {
          this.$message.warning('【' + label + '】请输入数字' + (isWeight ? '（0~100 的整数）' : '（可留空）'))
          return
        }
        if (n !== undefined && n < 0) {
          this.$message.warning('【' + label + '】不能小于 0')
          return
        }
        if (isWeight && n !== undefined && n % 1 !== 0) {
          this.$message.warning('【' + label + '】请填写 0~100 的整数')
          return
        }
        vals[key] = n
      }
      if (vals.aTarget === undefined && vals.aActual === undefined && vals.bTarget === undefined && vals.bActual === undefined) {
        this.$message.warning('请至少填写一项目标值或实际值再保存')
        return
      }
      if (vals.aWeight === undefined || vals.bWeight === undefined) {
        this.$message.warning('两项占比必填，且之和必须为 100')
        return
      }
      if (vals.aWeight + vals.bWeight !== 100) {
        this.$message.warning('两项占比之和必须为 100')
        return
      }
      const data = {
        year: Number(this.query.year),
        season: this.query.season,
        deptId: row.deptId,
        aTarget: vals.aTarget === undefined ? null : vals.aTarget,
        aActual: vals.aActual === undefined ? null : vals.aActual,
        bTarget: vals.bTarget === undefined ? null : vals.bTarget,
        bActual: vals.bActual === undefined ? null : vals.bActual,
        aWeight: vals.aWeight,
        bWeight: vals.bWeight,
        score: this.rowScore(row)
      }
      this.saving = row
      saveQuant(data).then(() => {
        this.saving = null
        this.$notify({ title: '成功', message: '定量数据已保存（含本期占比快照）', type: 'success', duration: 2000 })
        this.load()
      }).catch(() => {
        this.saving = null
      })
    },
    /** 删除行：未保存的新行直接移除；已保存行确认后删除该部门全部期次数据（占比已快照在各期数据行内，随之删除） */
    removeRow(row) {
      if (row.isNew) {
        this.rows = this.rows.filter(r => r !== row)
        return
      }
      this.$confirm('将删除【' + row.deptName + '】的定量数据（含全部期次与各期占比快照），且不可恢复，确认删除？', '删除整行', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.deleting = row
        deleteQuantRow(row.deptId).then(() => {
          this.deleting = null
          this.$notify({ title: '成功', message: '已删除', type: 'success', duration: 2000 })
          this.load()
        }).catch(() => {
          this.deleting = null
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* ===== 轻量视觉优化（沿用项目 element-ui 原有配色，不引入新色系） ===== */
::v-deep .el-table th {
  background: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
::v-deep .el-table tbody tr:hover > td {
  background: #ecf5ff !important;
}
::v-deep .el-button--primary {
  border-radius: 4px;
}
/* 配置页卡片：轻阴影 + 小圆角，卡头沿用默认白底 */
::v-deep .el-card {
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04) !important;
}
::v-deep .el-alert {
  border-radius: 4px;
}
</style>

